// @flow
import api from '../api/search';
import { createSelector } from 'reselect';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { getItem } from './item';
import { getItemCardDatas } from './cardData';
import { getUserExcludedFacets } from './searchExclusions';
import type { GlobalState } from '../rootReducer';
import { LOAD_SIMILAR_ITEMS_V3_SUCCESS } from './similarItemsV3';

/* Action Types */
export const CLEAR_PROMOTED_SEARCH_RESULTS = 'CLEAR_PROMOTED_SEARCH_RESULTS';
export const LOAD_PROMOTED_SEARCH_RESULTS_FAIL = 'LOAD_PROMOTED_SEARCH_RESULTS_FAIL';
export const LOAD_PROMOTED_SEARCH_RESULTS_REQUEST = 'LOAD_PROMOTED_SEARCH_RESULTS_REQUEST';
export const LOAD_PROMOTED_SEARCH_RESULTS_SUCCESS = 'LOAD_PROMOTED_SEARCH_RESULTS_SUCCESS';

// reducer
const DEFAULT_STATE = {
    all: [],
    itemIds: [],
};

export type State = {|
    ...typeof DEFAULT_STATE,
|};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}) {
    switch (action.type) {
        case LOAD_SIMILAR_ITEMS_V3_SUCCESS:
            const itemIds = action.payload.filter(({ itemId, promoted }) => promoted && itemId);
            return {
                ...state,
                all: [...new Set([...state.all, ...itemIds])], // prevent duplicates
            };
        case CLEAR_PROMOTED_SEARCH_RESULTS:
        case LOAD_PROMOTED_SEARCH_RESULTS_FAIL:
            return DEFAULT_STATE;
        case LOAD_PROMOTED_SEARCH_RESULTS_SUCCESS:
            return {
                ...state,
                all: [...new Set([...state.all, ...action.payload.itemIDs])], // prevent duplicates
                itemIds: action.payload.itemIDs,
            };
        case LOAD_PROMOTED_SEARCH_RESULTS_REQUEST:
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.searchPromoted;

export const getPromotedSearchResults = createSelector(stateSelector, state => state.itemIds || []);
export const getAllPromotedSearchResults = createSelector(stateSelector, state => state.all || []);

export const getPromotedItems = (state: GlobalState) => {
    const itemIds = getPromotedSearchResults(state);
    return getItemCardDatas(state, itemIds);
};

export const isItemPromoted = (state: GlobalState, itemId: number) => {
    const itemIds = getAllPromotedSearchResults(state);
    return itemIds.includes(itemId);
};

/* ACTION CREATORS */
export const clearPromotedSearchResults = () => ({
    payload: { itemIDs: [] },
    type: CLEAR_PROMOTED_SEARCH_RESULTS,
});

export const searchPromoted = ({ ids, pageName, searchQuery }: any) => async (
    dispatch: Function,
    getState: Function
) => {
    try {
        const state = getState();
        const deployment = getDeployment(state);
        const authToken = getAuthToken(state);

        if (pageName === 'catalog' || pageName === 'price-guide' || pageName === 'sold-search') {
            return dispatch(clearPromotedSearchResults());
        }
        dispatch({
            payload: searchQuery,
            type: LOAD_PROMOTED_SEARCH_RESULTS_REQUEST,
        });

        // Annotate facets which have user-defined search exclusions
        const searchExclusions = getUserExcludedFacets(state);
        const augmentedOptions = !searchExclusions
            ? searchQuery.options
            : Object.keys(searchExclusions).reduce(
                  (options, facetWithExclusions) => ({
                      ...options,
                      [facetWithExclusions]: [
                          {
                              exclude: searchExclusions[facetWithExclusions] || [],
                              include: (searchQuery.options && searchQuery.options[facetWithExclusions]) || [],
                          },
                      ],
                  }),
                  { ...searchQuery.options }
              );
        const augmentedSearchQuery = {
            ...searchQuery,
            options: augmentedOptions,
        };

        const results = await api.fetchPromotedSearchResults({
            authToken,
            deployment,
            ids,
            pageName,
            searchQuery: augmentedSearchQuery,
        });
        if (results.error) {
            dispatch({
                error: true,
                meta: { augmentedSearchQuery },
                payload: results.payload,
                type: LOAD_PROMOTED_SEARCH_RESULTS_FAIL,
            });
            return dispatch(clearPromotedSearchResults());
        } else {
            return dispatch({
                payload: results.payload,
                type: LOAD_PROMOTED_SEARCH_RESULTS_SUCCESS,
            });
        }
    } catch (error) {
        dispatch({
            error: true,
            meta: { searchQuery },
            payload: error,
            type: LOAD_PROMOTED_SEARCH_RESULTS_FAIL,
        });
        return dispatch(clearPromotedSearchResults());
    }
};

export const getDeletedItemTitleIfNeeded = (state: GlobalState, query: any) => {
    let item;
    let returnTitle = '';
    if (query.deleted && !isNaN(query.deleted)) {
        item = getItem(state, query.deleted) || {
            isDeleted: true,
            title: 'The item you were seeking',
        };
    }
    if (item && item.isDeleted && item.title) {
        returnTitle = item.title;
    }
    return returnTitle;
};

//TODO: rename all the categories to landing where appropriate
